/**
 * How the irrigation pages show a soil channel's state. The state itself is
 * the server's (HHNodeServer utils/soilMoisture.js): this file only words and
 * colours it, sums it per farm, and checks levels as they are typed.
 */
import type { MoistureStatus, OverviewController, OverviewFarm, SoilChannelState } from '../services/irrigationOverview';
import { relayDisplayName, usableRelays } from '../services/espnowIrrigation';
import type { MoistureLevels } from '../services/sensorDevices';

export type MoistureDirection = 'higher_is_drier' | 'lower_is_drier';

/** Mirrors the server: a Watermark's kPa rises as soil dries; a % probe's falls. */
export function directionFor(typeKey: string): MoistureDirection {
  return typeKey === 'soil_tension' ? 'higher_is_drier' : 'lower_is_drier';
}

export const STATUS_LABEL: Record<MoistureStatus, string> = {
  dry: 'Dry',
  drying: 'Needs water',
  ok: 'OK',
  wet: 'Wet',
  unset: 'No levels set',
  no_reading: 'No reading',
};

/** Tailwind classes for a status chip. */
export const STATUS_CHIP: Record<MoistureStatus, string> = {
  dry: 'bg-red-100 text-red-800 border-red-200',
  drying: 'bg-amber-100 text-amber-800 border-amber-200',
  ok: 'bg-green-100 text-green-800 border-green-200',
  wet: 'bg-sky-100 text-sky-800 border-sky-200',
  unset: 'bg-gray-100 text-gray-600 border-gray-200',
  no_reading: 'bg-gray-100 text-gray-600 border-gray-200',
};

/** Band colours for the chart, as rgba so the line stays readable over them. */
export const BAND_FILL = {
  dry: 'rgba(220, 38, 38, 0.10)',
  drying: 'rgba(217, 119, 6, 0.10)',
  wet: 'rgba(2, 132, 199, 0.10)',
};

/**
 * Typical Watermark levels for a starting point. Only a starting point: the
 * right levels depend on the soil, the crop and the depth, and the form says so.
 */
export const TYPICAL_WATERMARK_LEVELS: MoistureLevels = { wet: 10, refill: 40, dry: 60 };

/** A soil channel's own chart, a week by default. */
export function sensorChannelHref(deviceId: string, kind: string, index: number): string {
  return `/irrigation/sensors/${encodeURIComponent(deviceId)}?channel=${kind}-${index}&range=week`;
}

export interface ChannelRef {
  deviceId: string;
  deviceName: string;
  channel: SoilChannelState;
}

/** A farm's soil channels across all its probes, worst first. */
export function farmSoilChannels(farm: Pick<OverviewFarm, 'sensors'>): ChannelRef[] {
  const out: ChannelRef[] = [];
  for (const s of farm.sensors) {
    for (const ch of s.soil_channels ?? []) {
      out.push({ deviceId: s.device_id, deviceName: s.name || s.device_id, channel: ch });
    }
  }
  return out.sort((a, b) => compareStatus(a.channel, b.channel));
}

const RANK: Record<MoistureStatus, number> = { dry: 0, drying: 1, no_reading: 2, wet: 3, unset: 4, ok: 5 };

export function compareStatus(
  a: Pick<SoilChannelState, 'status' | 'stale'>,
  b: Pick<SoilChannelState, 'status' | 'stale'>
): number {
  return RANK[a.status] - RANK[b.status] || Number(b.stale) - Number(a.stale);
}

export interface SoilSummary {
  total: number;
  dry: number;
  drying: number;
  ok: number;
  wet: number;
  unset: number;
  silent: number;
}

/** Counts for a farm's chip. `silent` is no reading, or one gone stale. */
export function summarise(channels: SoilChannelState[]): SoilSummary {
  const s: SoilSummary = { total: channels.length, dry: 0, drying: 0, ok: 0, wet: 0, unset: 0, silent: 0 };
  for (const ch of channels) {
    if (ch.status === 'no_reading' || ch.stale) s.silent++;
    if (ch.status === 'dry') s.dry++;
    else if (ch.status === 'drying') s.drying++;
    else if (ch.status === 'ok') s.ok++;
    else if (ch.status === 'wet') s.wet++;
    else if (ch.status === 'unset') s.unset++;
  }
  return s;
}

/** What to call one channel on its own: "North probe · 30 cm". */
export function channelLabel(ref: ChannelRef): string {
  const own = ref.channel.name?.trim();
  return own ? `${ref.deviceName} · ${own}` : ref.deviceName;
}

export function formatReading(value: number | null, unit: string | null): string {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  const v = Number(value);
  const text = Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(1);
  return unit ? `${text} ${unit}` : text;
}

/**
 * Check levels as typed, the way the server will. Returns a sentence, or null
 * when they are fine. The server checks again and its answer wins.
 */
export function levelsProblem(
  levels: { wet: number | null; refill: number | null; dry: number | null },
  direction: MoistureDirection
): string | null {
  const { wet, refill, dry } = levels;
  if (refill == null) return 'Set the refill level.';
  if (dry == null) return 'Set the dry level.';
  if (direction === 'higher_is_drier') {
    if (!(dry > refill)) return 'The dry level has to read higher than refill (soil tension rises as it dries).';
    if (wet != null && !(wet < refill)) return 'The wet level has to read lower than refill.';
  } else {
    if (!(dry < refill)) return 'The dry level has to read lower than refill (soil water falls as it dries).';
    if (wet != null && !(wet > refill)) return 'The wet level has to read higher than refill.';
  }
  return null;
}

export interface ValveOption {
  routerMac: string;
  relayIndex: number;
  /** "Rows 1-12", as the other irrigation pages name it. */
  name: string;
  /** The node and site it is on, for telling two "Relay 0"s apart. */
  where: string;
}

/** Every valve on these controllers, named as the rest of the irrigation pages name it. */
export function valveOptions(controllers: OverviewController[]): ValveOption[] {
  const out: ValveOption[] = [];
  for (const c of controllers) {
    for (const r of c.routers) {
      for (const relay of usableRelays(r, c.transport)) {
        const zone = c.zones.find((z) => z.router_mac === r.router_mac && z.relay_index === relay);
        out.push({
          routerMac: r.router_mac,
          relayIndex: relay,
          name: relayDisplayName(zone?.zone_name, r.relay_labels, relay),
          where: `${r.name || r.router_mac} · ${c.controller_name || c.controller_id}`,
        });
      }
    }
  }
  return out;
}

/** The name of a linked valve, or null if it is not among these controllers. */
export function valveName(
  controllers: OverviewController[],
  valve: { router_mac: string; relay_index: number } | null | undefined
): string | null {
  if (!valve) return null;
  const mac = valve.router_mac.toUpperCase();
  const hit = valveOptions(controllers).find(
    (v) => v.routerMac.toUpperCase() === mac && v.relayIndex === valve.relay_index
  );
  return hit ? hit.name : null;
}
